/** core libraries & third party packages */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";

/** icons */
import { IoIosCheckmark } from "react-icons/io";

/** component */
import ProductCarousel from "../../components/core/productCardCarousel/ProductCarousel";

/** custom hooks & query & context */
import { useCartQuery, useProductQuery } from "../../hooks/useReactQuery";

/** utils */
import { handleGetUserFromLocalStorage } from "../../utils/common";

/** style */
import "./ProductDetails.scss";

/** interfaces */
import { IOrder } from "../../Interfaces/order";
import { IUser } from "../../Interfaces/user";
import { CartItem } from "../../Interfaces/cart";
import { IProduct, IVariantOption } from "../../Interfaces/product";

const ProductDetails = () => {
  const { productId } = useParams();
  const [productDetailsByIdData, setProductDetailsByIdData] =
    useState<IProduct>();
  const [selectedProductDetails, setSelectedProductDetails] = useState<{
    quantity: number;
    size: number;
    variant: IVariantOption;
    stock: number;
  }>({
    quantity: 1,
    size: 0,
    variant: {
      color: "",
      images: [],
      sizes: [],
    },
    stock: 0,
  });
  const [allCartItem, setAllCartItem] = useState<CartItem[]>();
  const [isPending, setIsPending] = useState(false);

  const currentUser = JSON.parse(
    handleGetUserFromLocalStorage() as string
  ) as IUser;

  const { Prefetch: prefetchGetAllCart, updateMutation } = useCartQuery();
  const getAllCarts = prefetchGetAllCart(currentUser?._id);
  const { PrefetchById: PrefetchProductById } = useProductQuery();
  const getProductById = PrefetchProductById(productId);

  useEffect(() => {
    if (
      productDetailsByIdData?.variantOptions !== undefined &&
      productDetailsByIdData?.variantOptions?.length > 0
    ) {
      setSelectedProductDetails((prev) => ({
        ...prev,
        variant: productDetailsByIdData.variantOptions[0],
        size: productDetailsByIdData.variantOptions[0].sizes[0]?.size,
        quantity: 1,
      }));
    }
  }, [productDetailsByIdData]);

  useEffect(() => {
    if (getAllCarts.data?.data !== undefined) {
      // console.log("hit");
      setAllCartItem(getAllCarts.data.data);
    }
  }, [getAllCarts.data?.data, isPending]);

  useEffect(() => {
    if (getProductById.data?.data !== undefined) {
      setProductDetailsByIdData(getProductById.data.data);
    }
  }, [getProductById.data]);

  const handleSelectColor = (selectedColorObjId: string) => {
    const newVariant = productDetailsByIdData?.variantOptions.find(
      (prev) => prev._id === selectedColorObjId
    );
    setSelectedProductDetails((prev) => ({
      ...prev,
      variant: newVariant as IVariantOption,
      size: newVariant?.sizes[0].size as number,
    }));
  };

  const handleAddToCart = async () => {
    if (!currentUser || currentUser.accessToken === null) {
      toast.error("You need to log in to add products to your cart.");
      return;
    }

    setIsPending(true);
    const newProductItem = {
      productId: productDetailsByIdData?._id as string,
      quantity: selectedProductDetails.quantity,
      size: selectedProductDetails.size as number,
      price: productDetailsByIdData?.price as number,
      color: selectedProductDetails.variant.color as string,
      variantImages: selectedProductDetails.variant.images,
    };

    const existingProductsMatch = allCartItem?.findIndex(
      (item) =>
        item.productId === productDetailsByIdData?._id &&
        item.size === selectedProductDetails.size &&
        item.color === selectedProductDetails.variant.color
    );

    // removed existing cart item _id and assigned
    let updatedCartItems =
      allCartItem?.map(({ _id, ...item }: CartItem) => ({
        ...item,
      })) || [];

    if (existingProductsMatch !== -1) {
      // If product already exists, update quantity

      // updatedCartItems[existingProductsMatch as number].quantity +=
      //   selectedProductDetails.quantity;

      toast.error("Product already added in cart");
      setIsPending(false);
      return;
    } else {
      updatedCartItems?.push(newProductItem);
    }

    if (updatedCartItems === undefined) {
      return;
    }

    const payload: IOrder = {
      userId: currentUser._id,
      products: updatedCartItems,
    };

    updateMutation
      .mutateAsync(payload)
      .then((res) => {
        console.log("res", res);
        setIsPending(false);
        toast.success("Product added to cart successfully!");
        getAllCarts.refetch?.();
      })
      .catch((err) => {
        console.log("error", err);
        setIsPending(false);
        toast.success("Failed to add item to the cart");
      });
  };

  return (
    <div className="min-h-[100vh]">
      {productDetailsByIdData !== undefined && (
        <div className="product_details_page lg:w-[96%]">
          <div className="product_details">
            <div className="product_images">
              <div className="product_side_images">
                <div className="grid grid-cols-2 md:gap-5 gap-2">
                  <div className="product_image">
                    <img
                      src={selectedProductDetails.variant.images[0]}
                      alt="img"
                      loading="lazy"
                      className="md:rounded-[50px_0px_0px_0px] rounded-[20px_0px_0px_0px]"
                    />
                  </div>
                  <div className="product_image">
                    <img
                      src={selectedProductDetails.variant.images[1]}
                      alt="img"
                      loading="lazy"
                      className="md:rounded-[0px_50px_0px_0px] rounded-[0px_20px_0px_0px]"
                    />
                  </div>
                  <div className="product_image">
                    <img
                      src={selectedProductDetails.variant.images[2]}
                      alt="img"
                      loading="lazy"
                      className="md:rounded-[0px_00px_0px_50px] rounded-[0px_00px_0px_20px]"
                    />
                  </div>
                  <div className="product_image">
                    <img
                      src={selectedProductDetails.variant.images[3]}
                      alt="img"
                      loading="lazy"
                      className="md:rounded-[0px_00px_50px_0px] rounded-[0px_00px_20px_0px]"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="product_info_container">
              <div className="product_info">
                <div className="product_name">
                  {productDetailsByIdData?.name}
                </div>
                {/* <div className="product_ratings">
                  <div className="review_star">
                    {[...Array(5)].map((star) => {
                      return (
                        <>
                          <span style={{ color: "#ffc107" }}>★</span>
                        </>
                      );
                    })}
                    <div className="product_rating">4.5/5</div>
                  </div>
                </div> */}
                <div className="product_description pt-2">
                  {/* This Nike Lunar Roam which is perfect for any occasion. Crafted
                from a soft and breathable fabric, it offers superior comfort
                and style. */}
                  {productDetailsByIdData?.description}
                </div>
                <div className="flex gap-2">
                  <div className="product_amount">
                    ₹{productDetailsByIdData?.price}
                  </div>
                  {/* <div className="product_amount text-[var(--text-gray-color)] line-through">
                    $300
                  </div>
                  <div className=" items-end flex">
                    <div className=" text-[var(--danger-text-color)] bg-[var(--danger-bg-color)] h-fit w-fit text-end px-2 rounded-xl">
                      -40%
                    </div>
                  </div> */}
                </div>
              </div>
              <div className="product_sizes ">
                <div className="choose_size_text">Select Colors</div>
                <div className="flex items-center gap-4 flex-wrap pt-3">
                  {productDetailsByIdData.variantOptions.map(
                    (variant, index) => {
                      return (
                        <div
                          className="filter_color cursor-pointer"
                          key={index}
                          onClick={() =>
                            handleSelectColor(variant?._id as string)
                          }
                        >
                          <div
                            className={`filter_item ${variant.color.toLowerCase()} 
                            ${
                              selectedProductDetails.variant?.color ===
                                variant.color && "border-2 border-red-500"
                            }
                            `}
                          >
                            {selectedProductDetails.variant?.color ===
                              variant.color && (
                              <div className="item_check">
                                <IoIosCheckmark />
                              </div>
                            )}
                          </div>
                          <div className="filter_item_label">
                            {variant.color}
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
              <div className="product_sizes">
                <div className="choose_size_text">Choose Size</div>
                <div className="flex gap-2 pt-3">
                  {selectedProductDetails.variant.sizes.map((size, index) => {
                    return (
                      <div key={index} className={`all_sizes pt-1 `}>
                        <div
                          className={`size ${
                            selectedProductDetails.size === size.size &&
                            "selected_size"
                          }`}
                          onClick={() => {
                            setSelectedProductDetails((prev) => ({
                              ...prev,
                              size: size.size,
                            }));
                          }}
                        >
                          {size.size}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <div>
                  <div className="flex items-center gap-5 mt-3">
                    <div className="bg-[var(--white-color)]  select-none  py-[7px] px-5 w-fit flex gap-4 items-center rounded-[13px]">
                      <div
                        className="text-[22px] cursor-pointer"
                        onClick={() => {
                          if (selectedProductDetails.quantity > 1) {
                            setSelectedProductDetails((prev) => ({
                              ...prev,
                              quantity: prev.quantity - 1,
                            }));
                          }
                        }}
                      >
                        -
                      </div>
                      <div className="text-[14px]">
                        {selectedProductDetails.quantity}
                      </div>
                      <div
                        className="text-[20px] cursor-pointer"
                        onClick={() => {
                          setSelectedProductDetails((prev) => ({
                            ...prev,
                            quantity: prev.quantity + 1,
                          }));
                        }}
                      >
                        +
                      </div>
                    </div>
                    <div
                      className="add_to_cart_btn button"
                      onClick={() => {
                        handleAddToCart();
                      }}
                    >
                      <button disabled={isPending}>
                        {isPending ? "Loading..." : "Add to Cart"}
                      </button>
                    </div>
                  </div>

                  {/* <div className="add_to_wishlist_btn">
                    <button>Buy it now</button>
                  </div> */}
                </div>
                <div className="pt-5">
                  <div className="text-[#232321] uppercase text-[16px] font-medium">
                    About the product
                  </div>
                  <div className="text-[#232321] text-[15px] font-normal pt-4">
                    {productDetailsByIdData.brand} /{" "}
                    {productDetailsByIdData.category}
                  </div>
                  <div className="text-[#232321] text-[15px] font-normal pt-1">
                    This product is excluded from all promotional discounts and
                    offers.
                  </div>
                  <div className="text-[#232321] text-[14px] font-normal pt-5">
                    <li>
                      Crafted with premium quality materials for enhanced
                      durability.
                    </li>
                    <li>Designed for maximum comfort and everyday use.</li>
                    <li>Tested for quality and long-lasting performance.</li>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-[80px] mx-[10px]">
            <ProductCarousel />
          </div>
        </div>
      )}

      {/* <section>
        <Reviews />
      </section> */}
    </div>
  );
};

export default ProductDetails;
